import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Modal, Input, message, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BASE_API_URL } from "../../utils/consts";
import { uploadFileToR2 } from "../../utils/uploadToR2";
import "./Documents.scss";

// ── Types ──────────────────────────────────────────────────────────────────────
type DocPermission = "view" | "upload" | "edit" | "delete";

type DocRole = {
  _id: string;
  name: string;
  permissions: DocPermission[];
};

type DocGroup = {
  _id: string;
  name: string;
  memberIds: string[];
};

// Backend shape — no local `id`
type FolderPermEntry = {
  entityType: "user" | "group";
  entityId: string;
  roleId: string;
};

// Modal state shape — adds local `id` for React keys
type PermissionEntry = FolderPermEntry & { id: string };

type Folder = {
  _id: string;
  name: string;
  parentId: string | null;
  docCount: number;
  subCount: number;
  isPublic?: boolean;
  permissions?: FolderPermEntry[];
};

type Doc = {
  _id: string;
  name: string;
  folderId: string | null;
  url: string;
  filename: string;
  mimetype: string;
  size: number;
  createdAt: string;
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatSize = (bytes?: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileType = (mimetype?: string, filename?: string) => {
  if (!mimetype) return "FILE";
  if (mimetype.startsWith("image/")) return "IMG";
  if (mimetype === "application/pdf") return "PDF";
  if (mimetype.includes("word")) return "DOC";
  if (mimetype.includes("excel") || mimetype.includes("spreadsheet")) return "XLS";
  return filename?.split(".").pop()?.toUpperCase() ?? "FILE";
};

// Build ancestor path for a folderId from a flat list
const buildPath = (
  folderId: string | null,
  all: Folder[]
): Array<{ _id: string | null; name: string }> => {
  if (!folderId) return [];
  const path: Folder[] = [];
  let cur: Folder | undefined = all.find((f) => f._id === folderId);
  while (cur) {
    path.unshift(cur);
    const pid = cur.parentId;
    cur = pid ? all.find((f) => f._id === pid) : undefined;
  }
  return path.map((f) => ({ _id: f._id, name: f.name }));
};

// ── Component ──────────────────────────────────────────────────────────────────
const Documents: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const clientId: string =
    typeof userData?.clientId === "object"
      ? userData.clientId._id
      : userData?.clientId;

  // ── State ────────────────────────────────────────────────────────────────────
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [docs, setDocs] = useState<Doc[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);

  // Permission data loaded once
  const [docRoles, setDocRoles] = useState<DocRole[]>([]);
  const [docGroups, setDocGroups] = useState<DocGroup[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // Folder modal (create / rename)
  const [folderModal, setFolderModal] = useState<{
    open: boolean;
    mode: "create" | "rename";
    folderId?: string;
    value: string;
    isPublic: boolean;
    permEntries: PermissionEntry[];
  }>({ open: false, mode: "create", value: "", isPublic: true, permEntries: [] });
  const [folderModalLoading, setFolderModalLoading] = useState(false);

  // Rename doc modal
  const [renameDocModal, setRenameDocModal] = useState<{
    open: boolean;
    docId: string;
    value: string;
  }>({ open: false, docId: "", value: "" });
  const [renameDocLoading, setRenameDocLoading] = useState(false);

  // Open folder context menu id (tile in right pane or tree item)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenuId]);

  // Delete confirmation
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    type: "folder" | "doc";
    id: string;
    name: string;
  }>({ open: false, type: "folder", id: "", name: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── Derived ──────────────────────────────────────────────────────────────────
  // Direct children of the selected folder (shown in right pane as folder tiles)
  const childFolders = allFolders.filter(
    (f) => (f.parentId ?? null) === (selectedFolderId ?? null)
  );

  // Breadcrumb path for right pane header
  const breadcrumb = [
    { _id: null, name: t("Documents") },
    ...buildPath(selectedFolderId, allFolders),
  ];

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchAllFolders = useCallback(() => {
    if (!clientId) return;
    setFoldersLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/document-folders?all=true`)
      .then((res) => setAllFolders(res.data))
      .catch((err) => console.error(err))
      .finally(() => setFoldersLoading(false));
  }, [clientId]);

  const fetchPermissionData = useCallback(() => {
    if (!clientId) return;
    Promise.all([
      axios.get(`${BASE_API_URL}/${clientId}/doc-roles`).catch(() => ({ data: [] })),
      axios.get(`${BASE_API_URL}/${clientId}/doc-groups`).catch(() => ({ data: [] })),
      axios.get(`${BASE_API_URL}/${clientId}/users`).catch(() => ({ data: [] })),
    ]).then(([rolesRes, groupsRes, usersRes]) => {
      setDocRoles(rolesRes.data);
      setDocGroups(groupsRes.data);
      setUsers(usersRes.data);
    });
  }, [clientId]);

  const fetchDocs = useCallback(() => {
    if (!clientId) return;
    setDocsLoading(true);
    const fParam = selectedFolderId ?? "null";
    axios
      .get(`${BASE_API_URL}/${clientId}/documents?folderId=${fParam}`)
      .then((res) => setDocs(res.data))
      .catch((err) => console.error(err))
      .finally(() => setDocsLoading(false));
  }, [clientId, selectedFolderId]);

  useEffect(() => { fetchAllFolders(); }, [fetchAllFolders]);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  useEffect(() => { fetchPermissionData(); }, [fetchPermissionData]);

  // ── Tree helpers ─────────────────────────────────────────────────────────────
  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // When selecting a folder from the right pane, also expand all its ancestors in the tree
  const selectFolder = (id: string | null) => {
    setSelectedFolderId(id);
    if (id) {
      const ancestors = new Set(expandedIds);
      let cur: Folder | undefined = allFolders.find((f) => f._id === id);
      while (cur?.parentId) {
        ancestors.add(cur.parentId);
        cur = allFolders.find((f) => f._id === cur!.parentId);
      }
      setExpandedIds(ancestors);
    }
  };

  // ── Tree render (recursive) ──────────────────────────────────────────────────
  const renderTree = (parentId: string | null, depth: number): React.ReactNode => {
    const nodes = allFolders.filter((f) => (f.parentId ?? null) === parentId);
    if (!nodes.length) return null;

    return nodes.map((folder) => {
      const hasChildren = allFolders.some((f) => f.parentId === folder._id);
      const isExpanded = expandedIds.has(folder._id);
      const isSelected = selectedFolderId === folder._id;

      const handleRowClick = () => {
        selectFolder(folder._id);
        if (hasChildren) toggleExpand(folder._id);
      };

      const treeMenuId = `tree-${folder._id}`;
      const treeMenuOpen = openMenuId === treeMenuId;

      return (
        <div key={folder._id} className="dm-tree-item-wrap">
          <div className={`dm-tree-item-row${isSelected ? " dm-tree-item-row--active" : ""}`}>
            <button
              className="dm-tree-item"
              style={{ paddingLeft: `${10 + depth * 16}px` }}
              onClick={handleRowClick}
              title={folder.name}
            >
              {/* Chevron */}
              <span className={`dm-tree-chevron${hasChildren ? "" : " dm-tree-chevron--hidden"}`}>
                <svg
                  width="12" height="12" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                  className="dm-tree-chevron-svg"
                  style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </span>

              {/* Folder icon */}
              <span className="dm-tree-folder-ic">
                {isExpanded ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <path d="M2 6a2 2 0 0 1 2-2h5l2 2h9a2 2 0 0 1 2 2v1H2V6z" opacity="0.7"/>
                    <path d="M2 9h20l-1.5 10a2 2 0 0 1-2 1.5H5.5A2 2 0 0 1 3.5 19L2 9z"/>
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <path d="M10 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2z" />
                  </svg>
                )}
              </span>

              <span className="dm-tree-name">{folder.name}</span>

              {folder.isPublic === false && (
                <span className="dm-tree-lock" title={t("Restricted")}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
              )}

              {(folder.docCount > 0 || folder.subCount > 0) && (
                <span className="dm-tree-count">{folder.subCount + folder.docCount}</span>
              )}
            </button>

            {/* ⋯ context menu on tree row */}
            <div className="dm-tree-menu-wrap" ref={treeMenuOpen ? menuRef : undefined}>
              <button
                className={`dm-tree-dots${treeMenuOpen ? " dm-tree-dots--open" : ""}`}
                onClick={(e) => { e.stopPropagation(); setOpenMenuId(treeMenuOpen ? null : treeMenuId); }}
                title={t("Options")}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
                </svg>
              </button>
              {treeMenuOpen && (
                <div className="dm-ctx-menu dm-ctx-menu--left">
                  <button className="dm-ctx-item" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); openRenameFolder(folder); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h4L20 8l-4-4L4 16z"/></svg>
                    {t("Edit & Permissions")}
                  </button>
                  <button className="dm-ctx-item dm-ctx-item--danger" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); confirmDelete("folder", folder._id, folder.name); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                    {t("Delete")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Children */}
          {hasChildren && isExpanded && (
            <div className="dm-tree-children" style={{ marginLeft: `${18 + depth * 16}px` }}>
              {renderTree(folder._id, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // ── Folder CRUD ──────────────────────────────────────────────────────────────
  const openCreateFolder = () =>
    setFolderModal({ open: true, mode: "create", value: "", isPublic: true, permEntries: [] });

  const openRenameFolder = (folder: Folder) =>
    setFolderModal({
      open: true,
      mode: "rename",
      folderId: folder._id,
      value: folder.name,
      isPublic: folder.isPublic !== false,
      permEntries: (folder.permissions ?? []).map((p) => ({ ...p, id: Math.random().toString(36).slice(2) })),
    });

  const addPermEntry = () =>
    setFolderModal((p) => ({
      ...p,
      permEntries: [
        ...p.permEntries,
        { id: Math.random().toString(36).slice(2), entityType: "user", entityId: "", roleId: "" },
      ],
    }));

  const updatePermEntry = (id: string, patch: Partial<PermissionEntry>) =>
    setFolderModal((p) => ({
      ...p,
      permEntries: p.permEntries.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    }));

  const removePermEntry = (id: string) =>
    setFolderModal((p) => ({
      ...p,
      permEntries: p.permEntries.filter((e) => e.id !== id),
    }));

  const submitFolderModal = () => {
    const name = folderModal.value.trim();
    if (!name) return;

    if (!folderModal.isPublic) {
      const incomplete = folderModal.permEntries.some(
        (e) => !e.entityId || !e.roleId
      );
      if (incomplete) {
        message.warning(t("Fill in all permission rows or remove incomplete ones"));
        return;
      }
    }

    setFolderModalLoading(true);

    const payload: any = { name };
    if (folderModal.mode === "create") {
      payload.parentId = selectedFolderId;
      payload.isPublic = folderModal.isPublic;
      if (!folderModal.isPublic) {
        payload.permissions = folderModal.permEntries.map(({ entityType, entityId, roleId }) => ({
          entityType, entityId, roleId,
        }));
      }
    } else {
      payload.isPublic = folderModal.isPublic;
      if (!folderModal.isPublic) {
        payload.permissions = folderModal.permEntries.map(({ entityType, entityId, roleId }) => ({
          entityType, entityId, roleId,
        }));
      }
    }

    const req =
      folderModal.mode === "create"
        ? axios.post(`${BASE_API_URL}/${clientId}/document-folders`, payload)
        : axios.put(
            `${BASE_API_URL}/${clientId}/document-folders/${folderModal.folderId}`,
            payload
          );

    req
      .then(() => {
        message.success(
          folderModal.mode === "create" ? t("Folder created") : t("Folder renamed")
        );
        setFolderModal({ open: false, mode: "create", value: "", isPublic: true, permEntries: [] });
        fetchAllFolders();
      })
      .catch(() => message.error(t("Error")))
      .finally(() => setFolderModalLoading(false));
  };

  // ── Document CRUD ────────────────────────────────────────────────────────────
  const openUpload = () => uploadInputRef.current?.click();

  const handleUploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    e.target.value = "";
    try {
      const uploads = await Promise.all(files.map((f) => uploadFileToR2(f, "documents", clientId)));
      await Promise.all(
        uploads.map((u) =>
          axios.post(`${BASE_API_URL}/${clientId}/documents`, {
            name: u.filename,
            folderId: selectedFolderId,
            handle: u.key,
            url: u.url,
            filename: u.filename,
            mimetype: u.mimetype,
            size: u.size,
          })
        )
      );
      message.success(t("Files uploaded"));
      fetchDocs();
      fetchAllFolders();
    } catch (err) {
      console.error(err);
      message.error(t("Upload failed"));
    }
  };

  const openRenameDoc = (doc: Doc) =>
    setRenameDocModal({ open: true, docId: doc._id, value: doc.name });

  const submitRenameDoc = () => {
    const name = renameDocModal.value.trim();
    if (!name) return;
    setRenameDocLoading(true);
    axios
      .put(`${BASE_API_URL}/${clientId}/documents/${renameDocModal.docId}`, { name })
      .then(() => {
        message.success(t("File renamed"));
        setRenameDocModal({ open: false, docId: "", value: "" });
        fetchDocs();
      })
      .catch(() => message.error(t("Error")))
      .finally(() => setRenameDocLoading(false));
  };

  // ── Delete ────────────────────────────────────────────────────────────────────
  const confirmDelete = (type: "folder" | "doc", id: string, name: string) =>
    setDeleteModal({ open: true, type, id, name });

  const executeDelete = () => {
    setDeleteLoading(true);
    const req =
      deleteModal.type === "folder"
        ? axios.delete(`${BASE_API_URL}/${clientId}/document-folders/${deleteModal.id}`)
        : axios.delete(`${BASE_API_URL}/${clientId}/documents/${deleteModal.id}`);

    req
      .then(() => {
        message.success(
          deleteModal.type === "folder" ? t("Folder deleted") : t("File deleted")
        );
        setDeleteModal({ open: false, type: "folder", id: "", name: "" });
        if (
          deleteModal.type === "folder" &&
          (deleteModal.id === selectedFolderId ||
            buildPath(selectedFolderId, allFolders).some(
              (p) => p._id === deleteModal.id
            ))
        ) {
          setSelectedFolderId(null);
        }
        fetchAllFolders();
        fetchDocs();
      })
      .catch(() => message.error(t("Error")))
      .finally(() => setDeleteLoading(false));
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const rightEmpty = childFolders.length === 0 && docs.length === 0 && !docsLoading;

  return (
    <div className="dm-page">
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        multiple
        style={{ display: "none" }}
        onChange={handleUploadFiles}
      />
      {/* ── Global top bar ─────────────────────────────────────────────── */}
      <div className="dm-topbar">
        <h1 className="dm-title">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          {t("Documents")}
        </h1>
        <div className="dm-topbar-actions">
          <button className="dm-btn dm-btn--ghost" onClick={openCreateFolder}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              <line x1="12" y1="11" x2="12" y2="17" /><line x1="9" y1="14" x2="15" y2="14" />
            </svg>
            {t("New folder")}
          </button>
          <button className="dm-btn dm-btn--primary" onClick={openUpload}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {t("Upload files")}
          </button>
        </div>
      </div>

      {/* ── Two-pane body ───────────────────────────────────────────────── */}
      <div className="dm-body">

        {/* LEFT: Folder tree sidebar */}
        <aside className="dm-sidebar">
          {/* Root: All Documents */}
          <button
            className={`dm-tree-root${selectedFolderId === null ? " dm-tree-root--active" : ""}`}
            onClick={() => selectFolder(null)}
          >
            <span className="dm-tree-root-ic">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M10 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2z" />
              </svg>
            </span>
            <span className="dm-tree-name">{t("All Documents")}</span>
          </button>

          {foldersLoading ? (
            <div className="dm-tree-loading">
              <div className="dm-spinner dm-spinner--sm" />
            </div>
          ) : (
            <div className="dm-tree">{renderTree(null, 0)}</div>
          )}
        </aside>

        {/* RIGHT: Content pane */}
        <main className="dm-content">
          {/* Breadcrumb */}
          <nav className="dm-breadcrumb">
            {breadcrumb.map((item, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <svg className="dm-breadcrumb-sep" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                )}
                <button
                  className={`dm-breadcrumb-item${idx === breadcrumb.length - 1 ? " dm-breadcrumb-item--active" : ""}`}
                  onClick={() => selectFolder(item._id)}
                  disabled={idx === breadcrumb.length - 1}
                >
                  {item.name}
                </button>
              </React.Fragment>
            ))}
          </nav>

          {docsLoading ? (
            <div className="dm-loading"><div className="dm-spinner" /></div>
          ) : rightEmpty ? (
            <div className="dm-empty">
              <div className="dm-empty-ic">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="dm-empty-title">
                {selectedFolderId ? t("This folder is empty") : t("No documents yet")}
              </div>
              <div className="dm-empty-sub">{t("Create a folder or upload files to get started")}</div>
              <div className="dm-empty-actions">
                <button className="dm-btn dm-btn--ghost" onClick={openCreateFolder}>{t("New folder")}</button>
                <button className="dm-btn dm-btn--primary" onClick={openUpload}>{t("Upload files")}</button>
              </div>
            </div>
          ) : (
            <div className="dm-sections">

              {/* Sub-folders */}
              {childFolders.length > 0 && (
                <section className="dm-section">
                  <h2 className="dm-section-title">
                    {t("Folders")}
                    <span className="dm-section-count">{childFolders.length}</span>
                  </h2>
                  <div className="dm-folders-grid">
                    {childFolders.map((folder) => {
                      const menuOpen = openMenuId === `tile-${folder._id}`;
                      return (
                        <div key={folder._id} className="dm-folder-card">
                          {/* Click area — navigates into folder */}
                          <button className="dm-folder-card-body" onClick={() => selectFolder(folder._id)}>
                            <span className="dm-folder-card-ic">
                              <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                <path d="M10 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2z" />
                              </svg>
                              {folder.isPublic === false && (
                                <span className="dm-folder-lock">
                                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                  </svg>
                                </span>
                              )}
                            </span>
                            <span className="dm-folder-card-name" title={folder.name}>{folder.name}</span>
                            <span className="dm-folder-card-meta">
                              {folder.subCount > 0 ? `${folder.subCount} ${t("folders")} · ` : ""}
                              {folder.docCount} {t("files")}
                            </span>
                          </button>

                          {/* ⋯ menu */}
                          <div className="dm-folder-card-menu-wrap" ref={menuOpen ? menuRef : undefined}>
                            <button
                              className={`dm-dots-btn${menuOpen ? " dm-dots-btn--open" : ""}`}
                              onClick={(e) => { e.stopPropagation(); setOpenMenuId(menuOpen ? null : `tile-${folder._id}`); }}
                              title={t("Options")}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
                              </svg>
                            </button>
                            {menuOpen && (
                              <div className="dm-ctx-menu">
                                <button className="dm-ctx-item" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); openRenameFolder(folder); }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h4L20 8l-4-4L4 16z"/></svg>
                                  {t("Edit & Permissions")}
                                </button>
                                <button className="dm-ctx-item dm-ctx-item--danger" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); confirmDelete("folder", folder._id, folder.name); }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                  </svg>
                                  {t("Delete")}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Files */}
              {docs.length > 0 && (
                <section className="dm-section">
                  <h2 className="dm-section-title">
                    {t("Files")}
                    <span className="dm-section-count">{docs.length}</span>
                  </h2>
                  <div className="dm-files-grid">
                    {docs.map((doc) => {
                      const isImage = doc.mimetype?.startsWith("image/");
                      const fileType = getFileType(doc.mimetype, doc.filename);
                      return (
                        <div key={doc._id} className="dm-file-tile">
                          <div className="dm-file-preview">
                            {isImage ? (
                              <img src={doc.url} alt={doc.name} className="dm-file-thumb" />
                            ) : (
                              <div className={`dm-file-ic dm-file-ic--${fileType.toLowerCase()}`}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                </svg>
                                <span className="dm-file-type-badge">{fileType}</span>
                              </div>
                            )}
                          </div>
                          <div className="dm-file-meta">
                            <div className="dm-file-name" title={doc.name}>{doc.name}</div>
                            {doc.size ? <div className="dm-file-size">{formatSize(doc.size)}</div> : null}
                          </div>
                          <div className="dm-tile-actions">
                            <a href={doc.url} target="_blank" rel="noreferrer" className="dm-tile-btn" title={t("Open")} onClick={(e) => e.stopPropagation()}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </a>
                            <button className="dm-tile-btn" title={t("Rename")} onClick={(e) => { e.stopPropagation(); openRenameDoc(doc); }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h4L20 8l-4-4L4 16z" /></svg>
                            </button>
                            <button className="dm-tile-btn dm-tile-btn--danger" title={t("Delete")} onClick={(e) => { e.stopPropagation(); confirmDelete("doc", doc._id, doc.name); }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

            </div>
          )}
        </main>
      </div>

      {/* ── Create / Rename Folder Modal ────────────────────────────────── */}
      <Modal
        open={folderModal.open}
        title={folderModal.mode === "create" ? t("New folder") : t("Rename folder")}
        okText={folderModal.mode === "create" ? t("Create") : t("Save")}
        cancelText={t("Cancel")}
        confirmLoading={folderModalLoading}
        onOk={submitFolderModal}
        onCancel={() => setFolderModal({ open: false, mode: "create", value: "", isPublic: true, permEntries: [] })}
        className="standardHeader standardFooter dm-folder-modal"
        width={540}
      >
        {/* Folder name */}
        <div className="dm-modal-field">
          <label className="dm-modal-label">{t("Folder name")}</label>
          <Input
            autoFocus
            placeholder={t("Folder name")}
            value={folderModal.value}
            onChange={(e) => setFolderModal((p) => ({ ...p, value: e.target.value }))}
            onPressEnter={folderModal.isPublic ? submitFolderModal : undefined}
            maxLength={80}
          />
        </div>

        {/* Access type */}
        <div className="dm-modal-field">
          <label className="dm-modal-label">{t("Access")}</label>
          <div className="dm-access-options">
            <button
              className={`dm-access-opt${folderModal.isPublic ? " dm-access-opt--active" : ""}`}
              onClick={() => setFolderModal((p) => ({ ...p, isPublic: true }))}
              type="button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              <div>
                <div className="dm-access-opt-title">{t("Public")}</div>
                <div className="dm-access-opt-desc">{t("All employees can view")}</div>
              </div>
            </button>
            <button
              className={`dm-access-opt${!folderModal.isPublic ? " dm-access-opt--active" : ""}`}
              onClick={() => setFolderModal((p) => ({ ...p, isPublic: false }))}
              type="button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <div>
                <div className="dm-access-opt-title">{t("Restricted")}</div>
                <div className="dm-access-opt-desc">{t("Only selected users / groups")}</div>
              </div>
            </button>
          </div>
        </div>

        {/* Permission entries (only when restricted) */}
        {!folderModal.isPublic && (
          <div className="dm-perm-section">
            <div className="dm-perm-section-head">
              <span className="dm-modal-label">{t("Who has access")}</span>
              <button className="dm-perm-add-btn" type="button" onClick={addPermEntry}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                {t("Add")}
              </button>
            </div>

            {folderModal.permEntries.length === 0 && (
              <div className="dm-perm-empty">{t("No access entries yet — click Add to assign access.")}</div>
            )}

            <div className="dm-perm-list">
              {folderModal.permEntries.map((entry) => {
                const entityOptions =
                  entry.entityType === "group"
                    ? docGroups.map((g) => ({ value: g._id, label: g.name }))
                    : users.map((u) => ({
                        value: u._id,
                        label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
                      }));

                return (
                  <div key={entry.id} className="dm-perm-row">
                    {/* Entity type toggle */}
                    <div className="dm-perm-type-btns">
                      <button
                        type="button"
                        className={`dm-perm-type-btn${entry.entityType === "user" ? " dm-perm-type-btn--active" : ""}`}
                        onClick={() => updatePermEntry(entry.id, { entityType: "user", entityId: "" })}
                        title={t("User")}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        className={`dm-perm-type-btn${entry.entityType === "group" ? " dm-perm-type-btn--active" : ""}`}
                        onClick={() => updatePermEntry(entry.id, { entityType: "group", entityId: "" })}
                        title={t("Group")}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      </button>
                    </div>

                    {/* Entity picker */}
                    <Select
                      placeholder={entry.entityType === "group" ? t("Select group") : t("Select user")}
                      value={entry.entityId || undefined}
                      onChange={(val) => updatePermEntry(entry.id, { entityId: val })}
                      options={entityOptions}
                      showSearch
                      filterOption={(input, opt) =>
                        (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())
                      }
                      className="dm-perm-entity-select"
                      size="small"
                    />

                    {/* Role picker */}
                    <Select
                      placeholder={t("Role")}
                      value={entry.roleId || undefined}
                      onChange={(val) => updatePermEntry(entry.id, { roleId: val })}
                      options={docRoles.map((r) => ({ value: r._id, label: r.name }))}
                      className="dm-perm-role-select"
                      size="small"
                    />

                    {/* Remove */}
                    <button
                      type="button"
                      className="dm-perm-remove-btn"
                      onClick={() => removePermEntry(entry.id)}
                      title={t("Remove")}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>

            {docRoles.length === 0 && (
              <div className="dm-perm-hint">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {t("No roles configured. Go to Settings → Documents to create roles first.")}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Rename Document Modal ────────────────────────────────────────── */}
      <Modal
        open={renameDocModal.open}
        title={t("Rename file")}
        okText={t("Save")}
        cancelText={t("Cancel")}
        confirmLoading={renameDocLoading}
        onOk={submitRenameDoc}
        onCancel={() => setRenameDocModal({ open: false, docId: "", value: "" })}
        className="standardHeader standardFooter"
      >
        <Input
          autoFocus
          placeholder={t("File name")}
          value={renameDocModal.value}
          onChange={(e) => setRenameDocModal((p) => ({ ...p, value: e.target.value }))}
          onPressEnter={submitRenameDoc}
          maxLength={120}
        />
      </Modal>

      {/* ── Delete Confirmation Modal ────────────────────────────────────── */}
      <Modal
        open={deleteModal.open}
        title={
          <span className="dm-delete-modal-title">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: "oklch(0.62 0.18 22)" }}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {deleteModal.type === "folder" ? t("Delete folder") : t("Delete file")}
          </span>
        }
        okText={t("Delete")}
        cancelText={t("Cancel")}
        confirmLoading={deleteLoading}
        onOk={executeDelete}
        onCancel={() => setDeleteModal({ open: false, type: "folder", id: "", name: "" })}
        okButtonProps={{ danger: true }}
        className="standardHeader standardFooter"
      >
        <p className="dm-delete-modal-body">
          {deleteModal.type === "folder"
            ? t("This will permanently delete the folder")
            : t("This will permanently delete the file")}{" "}
          <strong>"{deleteModal.name}"</strong>
          {deleteModal.type === "folder" && (
            <span className="dm-delete-modal-warn"> {t("and all its contents")}</span>
          )}
          {". "}{t("This action cannot be undone.")}
        </p>
      </Modal>
    </div>
  );
};

export default Documents;
